/**
 * Đọc thân một tin thành từng khối để chuông và trang "Thông báo của tôi" trình
 * bày cho dễ đọc (CEO chốt 1a · 3a, 31/10/2026).
 *
 * Thân tin là chữ thuần máy chủ chép từ mẫu Telegram: các dòng tách bằng xuống
 * dòng, bản tin tự dựng (Bất thường phát sinh, nghĩa vụ…) có dòng đầu nhóm kèm
 * số "(9)", dòng gạch "• …" và dòng "…và N dòng nữa". Trước đây màn in cả thân
 * thành một đoạn — xuống dòng mất hết, 9 khoản chạy liền thành một khối.
 */

export type BodySection = {
  /** Dòng đầu nhóm, đã bỏ biểu tượng cảm xúc ở đầu. Rỗng = các gạch đứng một mình. */
  title: string;
  /** Số trong "(9)" ở cuối dòng đầu nhóm — có thể lớn hơn số gạch đang có. */
  count?: number;
  items: string[];
  /** Số trong "…và N dòng nữa": các dòng máy chủ đã cắt bớt, thân tin không có. */
  more?: number;
};

export type BodyBlock = { kind: "text"; text: string } | ({ kind: "section" } & BodySection);

const NAMED: Record<string, string> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  nbsp: " ",
};

/**
 * Đổi ngược mã ký tự HTML. Máy chủ đã sửa từ 31/10/2026, nhưng tin lưu trước
 * đó vẫn còn nguyên "&#39;Tóc phim&#39;" trong hộp thư.
 */
export function decodeEntities(s: string): string {
  return s.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (m, code: string) => {
    if (code[0] === "#") {
      const n =
        code[1] === "x" || code[1] === "X"
          ? parseInt(code.slice(2), 16)
          : parseInt(code.slice(1), 10);
      return Number.isFinite(n) && n > 0 && n <= 0x10ffff ? String.fromCodePoint(n) : m;
    }
    return NAMED[code.toLowerCase()] ?? m;
  });
}

// Biểu tượng cảm xúc (kèm dấu biến thể / nối / màu da) ở ĐẦU chuỗi. Không dùng
// \p{Emoji}: lớp đó gồm cả chữ số — "3 đơn kẹt" sẽ mất số 3.
const LEADING_EMOJI =
  /^(?:(?:\p{Extended_Pictographic}|[\u{1F1E6}-\u{1F1FF}])(?:\u{FE0F}|\u{200D}|[\u{1F3FB}-\u{1F3FF}])*\s*)+/u;

/** Bỏ biểu tượng cảm xúc đầu dòng — màn đã có biểu tượng theo nhóm tin. */
export function stripLeadingEmoji(s: string): string {
  const out = s.replace(LEADING_EMOJI, "").trim();
  return out || s.trim();
}

/** Tiêu đề hiện trên màn: đổi ngược mã ký tự, bỏ biểu tượng cảm xúc đầu dòng. */
export function displayTitle(title: string): string {
  return stripLeadingEmoji(decodeEntities(title));
}

const BULLET = /^[•·▪◦‣-]\s+/;
const MORE = /^…\s*và\s+(\d+)\s+dòng nữa/;
const COUNT = /\s*\((\d+)\)\s*$/;

export function parseNotificationBody(body: string): BodyBlock[] {
  const lines = decodeEntities(body)
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  const blocks: BodyBlock[] = [];
  let section: ({ kind: "section" } & BodySection) | null = null;
  const isBullet = (l: string | undefined) => l !== undefined && BULLET.test(l);

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i] ?? "";
    if (isBullet(line)) {
      if (!section) {
        section = { kind: "section", title: "", items: [] };
        blocks.push(section);
      }
      section.items.push(line.replace(BULLET, ""));
      continue;
    }
    const more = MORE.exec(line);
    if (more && section) {
      section.more = Number(more[1]);
      section = null;
      continue;
    }
    // Dòng thường đứng ngay trước một gạch là đầu nhóm.
    if (isBullet(lines[i + 1])) {
      const count = COUNT.exec(line);
      const head: { kind: "section" } & BodySection = {
        kind: "section",
        title: stripLeadingEmoji(count ? line.slice(0, count.index) : line),
        items: [],
      };
      if (count) head.count = Number(count[1]);
      section = head;
      blocks.push(head);
      continue;
    }
    section = null;
    const last = blocks[blocks.length - 1];
    if (last?.kind === "text") last.text += "\n" + line;
    else blocks.push({ kind: "text", text: line });
  }
  return blocks;
}

/** Số dòng thật của một nhóm: số "(9)" nếu có, không thì gạch + dòng cắt bớt. */
export function sectionTotal(s: BodySection): number {
  return s.count ?? s.items.length + (s.more ?? 0);
}

/** Mã đơn trong một dòng (CA-2609-199, DN-264, PF-2502-001) — để in đậm. */
export const ORDER_CODE = /\b[A-Z]{2,5}-\d{2,4}(?:-\d{1,4})?\b/g;

/** "Hôm nay" / "Hôm qua" / "dd/mm/yyyy" theo giờ máy — chuông chia tin theo ngày. */
export function dayLabel(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  const key = (x: Date) => `${x.getFullYear()}-${x.getMonth()}-${x.getDate()}`;
  if (key(d) === key(now)) return "Hôm nay";
  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  if (key(d) === key(y)) return "Hôm qua";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`;
}
